import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Simple, clean price formatting function using Intl.NumberFormat for consistency.
 * Formats a number (representing smallest currency unit, e.g., cents) into a clean currency string.
 * @param amount - The amount in the smallest currency unit (e.g., cents for USD/EUR).
 * @param currency - The 3-letter ISO currency code (e.g., 'USD', 'EUR').
 * @param locale - Optional locale string (e.g., 'en-US', 'fr-FR'). Defaults to undefined (browser default).
 * @returns The formatted currency string (e.g., "$49.99", "29€"), or an empty string if amount is null/undefined.
 */
export function formatPrice(
  amount: number | null | undefined,
  currency: string,
  locale?: string
): string {
  if (amount === null || typeof amount === 'undefined') {
    return '';
  }

  const majorUnitAmount = amount / 100;
  
  // Use Intl.NumberFormat for consistent, localized formatting
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(majorUnitAmount);
    
    // Remove unwanted spaces around Euro symbol for tighter formatting
    if (currency.toUpperCase() === 'EUR') {
      return formatted.replace(/\s€/g, '€').replace(/€\s/g, '€');
    }
    
    return formatted;
  } catch (error) {
    // Simple fallback if Intl fails
    return `${majorUnitAmount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}
